<template>
  <div class="mt-[-3.75remX]">
    <FormKit id="changePassword" ref="formRef" name="changePassword" type="form" @submit="onSave">
      <FormStepInputs
        :step="{
          stepName: name,
          formInputs: content,
        }"
      >
        <template #cardFormatMessage>
          <div class="max-w-[704px] mt-5X">
            <InformationBox
              :title="cardFormatMessage.title.value"
              class="flex items-start gap-4 p-5 px-6 self-stretch"
              :description="cardFormatMessage.content.value"
              :type="cardFormatMessage.messageType.value"
              :isCircle="true"
              :useButtonSlot="true"
              :hideIcon="false"
            >
              <template #buttons>
                <div class="flex w-[640px] items-start content-start gap-4 flex-wrap">
                  <div class="sm:w-full md:w-auto">
                    <button
                      type="button"
                      class="link text-m"
                      data-test-id="Membership_Card_Modal_Order_Physical_Card"
                      @click="onOrderPhysicalCardClick()"
                    >
                      {{ getFormInput(props.content, 'orderPhysicalCardButtonText')?.label.value }}
                    </button>
                  </div>
                </div>
              </template>
            </InformationBox>
          </div>
        </template>
        <template #futureCardsTitle>
          <div class="mt-10">
            <PanelTitle v-bind="getFormInput(props.content, 'futureCardsTitle')"></PanelTitle>
          </div>
        </template>
        <template #cardOptionsLabel>
          <div class="mt-6">
            <FormKit
              v-model="model.deliveryChannelMembershipCard"
              :label="getFormInput(props.content, 'cardOptionsLabel')?.label?.value"
              :innerClass="'text-base'"
              inputClass="text-lg"
              type="nativeRadio"
              name="deliveryChannelNotices"
              outerClass="-mt-1"
              :options="getOptionsForInput()"
              @input="(e) => onOptionChange(e)"
            ></FormKit>
          </div>
        </template>
      </FormStepInputs>
      <div class="flex w-[640px] mt-14 items-start content-start gap-4 flex-wrap">
        <Link :field="discardChangesLink?.link" class="btn btn-tertiary btn-md">
          <div class="flex items-center space-x-2">
            <img src="@/assets/icons/arrow-left.svg" alt="left arrow" class="w-5 h-5 inline-block" />
            <span>{{ discardChangesLink?.link?.value?.text }}</span>
          </div>
        </Link>
        <button type="button" :disabled="!hasChanges" class="btn btn-md btn-primary" @click="onSave">
          Save changes
        </button>
      </div>
      <div class="!mt-10 mb-10 md:!mt-12 md:!mb-12">
        <Divider></Divider>
      </div>
    </FormKit>
    <FullScreenSpinner v-if="showSavingOrLoading" :applyOpacity="true"></FullScreenSpinner>
    <MembershipCardModal
      ref="membershipCardModalModalEl"
      :membershipCardModal="physicalCardStore.membershipCardModal"
    />
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, nextTick } from 'vue';
import { ISitecoreFormGenericInput } from '@/interfaces';
import { getFormInput } from '@/utils/form-input-helper';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import Divider from '@/components/Divider/Divider.vue';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import MembershipCardModal from '@/components/MembershipCard/MembershipCardModal.vue';
import { useSelfServicePhysicalMembershipCardStore, useAccountStore, useAnalyticsStore } from '@/store';
import { SelfServiceService } from '@/services/store';
import {
  CommunicationPreferenceOptions,
  CommunicationPreferences,
  getEnumValue,
} from '@/interfaces/self-service/communication-preferences';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { FormEvents } from '@/enums/form-events.enum';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { useI18n } from 'vue-i18n';
import { Link } from '@sitecore-jss/sitecore-jss-vue';
import { useRoute } from 'vue-router';

const physicalCardStore = useSelfServicePhysicalMembershipCardStore();
const accountStore = useAccountStore();
const toastAlertStore = useToastAlertStore();
const analyticsStore = useAnalyticsStore();

const route = useRoute();

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();

const membershipCardModalModalEl = ref<InstanceType<typeof MembershipCardModal>>();
const showSavingOrLoading = ref<boolean>(false);
const hasChanges = ref<boolean>(false);

const model = ref<any>({ deliveryChannelMembershipCard: '' });
const subscriptions = <any>[];
const cardFormatMessage = ref(getFormInput(props.content, 'cardFormatMessage'));
const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));

const cardFormatOptions = [
  { label: 'Physical card', value: CommunicationPreferenceOptions[CommunicationPreferenceOptions.PostalPhysical] },
  { label: 'Digital card', value: CommunicationPreferenceOptions[CommunicationPreferenceOptions.DigitalEmail] },
];

const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

onMounted(async () => {
  await loadPreference();

  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.classList.add('grow-0');
  }

  if (route.query.showcard !== undefined) {
    onViewDigitalCardClick();
  }

  pushAnalytics();
});

const loadPreference = async () => {
  showSavingOrLoading.value = true;
  try {
    const d365Id = accountStore.identityDetails?.d365Id ?? '';
    if (d365Id) {
      subscriptions.values = await SelfServiceService.getSubscriptions(d365Id);

      model.value.deliveryChannelMembershipCard = getSelectedValueByValue(
        subscriptions.values.deliveryChannelMembershipCard
      );
    }
  } catch (error) {
    console.error(error);
  }

  await nextTick();
  showSavingOrLoading.value = false;
};

const onOptionChange = (e: any) => {
  if (showSavingOrLoading.value) return;
  hasChanges.value = true;
};

const getOptionsForInput = () => {
  return cardFormatOptions.map((opt) => {
    return opt.label;
  });
};

const onSave = async () => {
  try {
    const submitModel = {
      deliveryChannelMembershipCard: getSelectedValueByDisplayValue(model.value.deliveryChannelMembershipCard),
    } as CommunicationPreferences;

    if (!submitModel.deliveryChannelMembershipCard) {
      return;
    }

    showSavingOrLoading.value = true;

    const d365Id = accountStore.identityDetails?.d365Id ?? '';

    if (d365Id) {
      const response = await SelfServiceService.updateMembershipCardFormatPref(d365Id, submitModel);

      if (response) {
        hasChanges.value = false;
        showSavingOrLoading.value = false;
        toastAlertStore.addToastAlert({
          title: 'Membership card preferences updated',
          description: '',
          type: ToastAlertType.SUCCESS,
          duration: 6000,
        });

        await analyticsStore.pushAnalytics({
          event: FormEvents.COMPLETE,
          form: {
            name: 'Membership card',
            stepName: 'FormCompleted',
          },
          profileInfo: {
            memCardFormatPref: model.value.deliveryChannelMembershipCard,
          },
        });

        return;
      }
    }
  } catch (error) {
    showSavingOrLoading.value = false;
    toastAlertStore.addToastAlert({
      title: 'Unable to save changes',
      description: '',
      type: ToastAlertType.ERROR,
      duration: 6000,
    });

    await discardChanges();

    await analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Membership card',
        formErrorType: 'Unable to save changes',
        formErrorDetail: 'Unable to save changes',
      },
    });
  }
};

const discardChanges = async () => {
  await loadPreference();
};

const getSelectedValueByDisplayValue = (selectedOption: string) => {
  const selected = cardFormatOptions.find((x) => x.label.toLowerCase() === selectedOption?.toLowerCase());
  return selected?.value;
};

const getSelectedValueByValue = (value: string) => {
  const enumValue = getEnumValue(value);
  if (enumValue !== undefined) {
    const selected = cardFormatOptions.find((x) => x.value === CommunicationPreferenceOptions[enumValue]);
    return selected?.label;
  }
  return '';
};

const onViewDigitalCardClick = () => {
  if (!membershipCardModalModalEl?.value) return;
  membershipCardModalModalEl?.value.showModal();
};

const onOrderPhysicalCardClick = () => {
  setTimeout(() => {
    if (!membershipCardModalModalEl?.value) return;
    membershipCardModalModalEl?.value.showOrderPhysicalCardModal();
  }, 100);
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);
  pageAnalytics.pushAnalytics();
};
</script>
